import React, { createContext, useContext, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { API } from '@/config/constants';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';

const POLL_MS = 30_000;
export const ARRANGEMENT_PHOTO_TIMEOUT_MS = 15_000;
const Context = createContext(null);
const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  && ['cloud', 'offline'].includes(binding.source)
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
export const arrangementPhotoIdentity = photo => photo?.storage_path || null;
const validPhoto = photo => photo === null || (typeof photo === 'object' && typeof photo?.storage_path === 'string' && !!photo.storage_path);

// Scoped to a mounted Design/session, never module-global. Aborting browser
// waiting cannot undo server work. Retired owners cannot regain UI authority.
function createOwner(binding, environment) {
  const capturedPrincipal = principal(binding);
  const revision = binding?.revision;
  const entries = new Map();
  const listeners = new Set();
  let version = 0;
  let active = false;
  let epoch = 0;
  let interval;
  const owns = () => active && !!capturedPrincipal && getActiveSessionRevision() === revision
    && environment.current.binding?.revision === revision && principal(environment.current.binding) === capturedPrincipal;
  const online = () => owns() && binding.source === 'cloud' && !isDesktopOffline();
  const emit = () => { version += 1; listeners.forEach(listener => listener()); };
  const eligible = (entry, kind) => {
    const user = environment.current.binding?.user;
    const roles = user?.roles || [];
    return kind === 'upload' ? roles.includes('admin') || roles.includes('designer')
      : roles.includes('designer') && (roles.includes('admin') || entry.assignedDesignerId === user.id);
  };
  const entryFor = (id, photo) => {
    if (!entries.has(id)) entries.set(id, {
      id, photo: photo || null, assignedDesignerId: null, users: 0, busy: false,
      status: 'idle', message: '', file: null, kind: null, uncertain: false, request: null,
    });
    return entries.get(id);
  };
  const publishPhoto = (entry, photo, current) => {
    if (!current()) return;
    const confirmedEpoch = epoch;
    const receiptOwns = () => owns() && epoch === confirmedEpoch;
    entry.photo = photo;
    try { environment.current.onPhotoConfirmed?.(entry.id, photo, receiptOwns); }
    catch { entry.message += ' The result is confirmed, but the shared display could not update. Refresh before another change.'; }
  };
  const cancel = entry => { entry.request?.controller.abort(); entry.request = null; };
  const begin = (entry, status) => {
    if (!online() || entry.busy || !entry.id) return null;
    const token = environment.current.getToken?.();
    if (!token || !online()) {
      if (owns()) { entry.message = 'Sign in again before changing or checking this photo.'; emit(); }
      return null;
    }
    cancel(entry);
    const ticket = { epoch, controller: new AbortController(), poll: status === 'polling' };
    entry.request = ticket;
    const current = () => owns() && epoch === ticket.epoch && entry.request === ticket;
    if (!ticket.poll) { entry.busy = true; entry.status = status; }
    entry.message = status === 'uploading' ? 'Uploading photo…' : status === 'deleting' ? 'Deleting photo…' : status === 'checking' ? 'Checking saved photo…' : entry.message;
    emit();
    return { ticket, current, headers: { Authorization: `Bearer ${token}` } };
  };
  const bounded = async (operation, ticket) => {
    let timeout;
    let abortListener;
    const stopped = new Promise((_, reject) => {
      abortListener = () => reject(new Error('Photo request stopped'));
      ticket.controller.signal.addEventListener('abort', abortListener, { once: true });
      timeout = setTimeout(() => ticket.controller.abort(), ARRANGEMENT_PHOTO_TIMEOUT_MS);
    });
    try { return await Promise.race([operation(), stopped]); }
    finally {
      clearTimeout(timeout);
      ticket.controller.signal.removeEventListener('abort', abortListener);
    }
  };
  const finish = (entry, request) => {
    if (!request.current()) return;
    entry.request = null;
    entry.busy = false;
    emit();
  };
  const read = async (entry, polling = false) => {
    if (polling && (entry.photo || entry.request || entry.status !== 'idle' || !entry.users)) return false;
    if (!polling && !['unknown', 'checked'].includes(entry.status)) return false;
    const request = begin(entry, polling ? 'polling' : 'checking');
    if (!request) return false;
    try {
      const photo = await bounded(async () => {
        const response = await fetch(`${API}/orders/${encodeURIComponent(entry.id)}`, {
          headers: request.headers, signal: request.ticket.controller.signal,
        });
        if (!response.ok) throw new Error('Photo refresh failed');
        const data = await response.json();
        // GET returns a raw order; deletion unsets the optional photo field.
        const value = data?.arrangement_photo === undefined ? null : data.arrangement_photo;
        if (data?.id !== entry.id || !validPhoto(value)) throw new Error('Invalid photo readback');
        return value;
      }, request.ticket);
      if (!request.current()) return false;
      if (!polling) {
        entry.status = 'checked';
        entry.message = 'The current saved photo is shown. The earlier request may still finish; this check does not prove which upload produced the photo. Retry only if you want to send the change again.';
      } else entry.message = '';
      publishPhoto(entry, photo, request.current);
      return true;
    } catch {
      if (request.current()) {
        if (!polling) entry.status = 'unknown';
        entry.message = polling ? 'The saved photo could not be refreshed. The displayed state has not been changed.'
          : 'The saved photo could not be checked. The earlier request may have succeeded. Check again before retrying.';
      }
      return false;
    } finally { finish(entry, request); }
  };
  const mutate = async (entry, kind, file, explicit = false, expectedPhoto) => {
    if (!online() || entry.busy || !eligible(entry, kind)) return false;
    if (!(explicit ? ['rejected', 'checked'] : ['idle']).includes(entry.status)) return false;
    if (kind === 'upload' && !file) return false;
    if (kind === 'delete' && (!entry.photo || (expectedPhoto !== undefined && arrangementPhotoIdentity(expectedPhoto) !== arrangementPhotoIdentity(entry.photo)))) {
      entry.message = 'The displayed photo changed. Review it before confirming deletion again.'; emit(); return false;
    }
    const request = begin(entry, kind === 'upload' ? 'uploading' : 'deleting');
    if (!request) return false;
    entry.kind = kind;
    entry.file = kind === 'upload' ? file : null;
    emit();
    try {
      const photo = await bounded(async () => {
        const body = kind === 'upload' ? new FormData() : undefined;
        if (body) body.append('file', file);
        const response = await fetch(`${API}/orders/${encodeURIComponent(entry.id)}/arrangement-photo`, {
          method: kind === 'upload' ? 'POST' : 'DELETE', headers: request.headers,
          ...(body ? { body } : {}), signal: request.ticket.controller.signal,
        });
        if (!response.ok) {
          const rejected = response.status >= 400 && response.status < 500 && response.status !== 408;
          const data = await response.json().catch(() => ({}));
          const error = new Error(typeof data.detail === 'string' ? data.detail : 'The photo change was rejected.');
          error.rejected = rejected;
          throw error;
        }
        const result = await response.json();
        if (kind === 'delete') {
          if (result?.deleted !== true) throw new Error('Unusable deletion acknowledgement');
          return null;
        }
        if (!result || !validPhoto(result)) throw new Error('Unusable upload acknowledgement');
        return result;
      }, request.ticket);
      if (!request.current()) return false;
      entry.status = 'idle';
      entry.message = kind === 'upload' ? 'Photo uploaded.' : 'Photo deleted.';
      entry.file = null; entry.kind = null; entry.uncertain = false;
      publishPhoto(entry, photo, request.current);
      return true;
    } catch (error) {
      if (request.current()) {
        entry.status = error.rejected ? 'rejected' : 'unknown';
        entry.uncertain = !error.rejected;
        entry.message = error.rejected ? `${error.message} Retry when ready.`
          : 'The photo change is not confirmed; it may have succeeded. Check the saved photo before retrying. The earlier request may still finish.';
      }
      return false;
    } finally { finish(entry, request); }
  };
  const incoming = (entry, photo) => {
    if (!owns()) return;
    if (arrangementPhotoIdentity(entry.photo) === arrangementPhotoIdentity(photo)) return;
    if (entry.busy && entry.status === 'checking') {
      cancel(entry);
      entry.busy = false;
      entry.status = 'unknown';
      entry.message = 'The saved photo changed while it was being checked. Review the current photo and check again before retrying.';
    }
    // A list refresh may observe another client's photo while our write is
    // pending. Keep that observation without releasing the write lock; a later
    // acknowledgement supersedes it, but rejection/uncertainty must not lose it.
    if (entry.request?.poll) cancel(entry);
    entry.photo = photo || null; emit();
  };
  return {
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    snapshot: () => version,
    owns, online, eligible, entryFor, incoming,
    start: () => {
      active = true;
      interval = setInterval(() => { entries.forEach(entry => { if (online()) void read(entry, true); }); }, POLL_MS);
      emit();
      return () => {
        active = false; epoch += 1; clearInterval(interval);
        entries.forEach(entry => { cancel(entry); entry.file = null; });
      };
    },
    retire: () => { if (!owns()) entries.forEach(entry => { cancel(entry); entry.file = null; }); },
    attach: entry => { entry.users += 1; return () => { entry.users -= 1; if (!entry.users && entry.request?.poll) cancel(entry); }; },
    reconcile: (orders, previous) => {
      orders?.forEach(order => {
        const identity = arrangementPhotoIdentity(order.arrangement_photo);
        if (previous.has(order.id) && previous.get(order.id) !== identity && entries.has(order.id)) incoming(entries.get(order.id), order.arrangement_photo);
        previous.set(order.id, identity);
      });
    },
    upload: (entry, file) => mutate(entry, 'upload', file),
    remove: (entry, expectedPhoto) => mutate(entry, 'delete', null, false, expectedPhoto),
    retry: (entry, expectedPhoto) => entry.kind ? mutate(entry, entry.kind, entry.file, true, expectedPhoto) : Promise.resolve(false),
    check: entry => read(entry),
    discard: entry => {
      if (!owns()) return;
      entry.file = null;
      if (!entry.busy && ['rejected', 'checked'].includes(entry.status)) {
        entry.status = 'idle'; entry.kind = null;
        entry.message = entry.uncertain ? 'Selection discarded. The earlier request may still finish.' : '';
      }
      emit();
    },
  };
}

function useOwner(onPhotoConfirmed, enabled = true) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const environment = useRef(null);
  environment.current = { binding: sessionBinding, getToken, onPhotoConfirmed };
  const [owner] = useState(() => createOwner(sessionBinding, environment));
  useLayoutEffect(() => enabled ? owner.start() : undefined, [owner, enabled]);
  useLayoutEffect(() => owner.retire(), [owner, revision, sessionBinding]);
  return owner;
}

export function ArrangementPhotoProvider({ children, onPhotoConfirmed, orders }) {
  const owner = useOwner(onPhotoConfirmed);
  const incoming = useRef(new Map());
  useLayoutEffect(() => owner.reconcile(orders, incoming.current), [owner, orders]);
  return <Context.Provider value={owner}>{children}</Context.Provider>;
}

export function useArrangementPhotoOwner() {
  const shared = useContext(Context);
  // Standalone viewers retain the same fail-closed contract without page-global state.
  const local = useOwner(undefined, !shared);
  return shared || local;
}
